import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, dateStrOf, todayStr } from "@/lib/tz";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { UserAvatar, fmtMinutes, TagChip } from "@/components/TaskShared";
import { SavedViews } from "@/components/SavedViews";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";
import { ManageSchedule } from "@/components/workload/ManageSchedule";
import { CalendarRange } from "lucide-react";
import { DateRangePicker } from "@/components/DateRangePicker";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  ArrowUpDown, Users2, Filter, ChevronLeft, ChevronRight, Gauge, ListChecks, Check, X, Clock, Timer, Loader2, Bell,
} from "lucide-react";
import {
  eachDayOfInterval, format, isWeekend, isToday, startOfWeek, addDays, differenceInCalendarDays, parseISO, isValid,
} from "date-fns";

const iso = (d) => format(d, "yyyy-MM-dd");
const hoursLabel = (min) => `${+((min || 0) / 60).toFixed(1)}h`;
const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const dowKey = (d) => DOW_KEYS[d.getDay()];

function MultiUserSelect({ users, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const label = selected.length === 0 ? "All users" : `${selected.length} user${selected.length > 1 ? "s" : ""}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="workload-user-filter" className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${selected.length ? "bg-[#EDE7FF] text-[#5b2fd6] border-[#d9ccff]" : "bg-white text-[#595D62] border-[#EEEFEF] hover:bg-[#F6F6FD]"}`}>
          <Users2 className="h-3.5 w-3.5" /> {label}
          {selected.length > 0 && <span role="button" tabIndex={0} data-testid="workload-user-filter-clear" onClick={(e) => { e.stopPropagation(); onChange([]); }} className="ml-0.5 rounded-full hover:bg-white/60 p-0.5"><X className="h-3 w-3" /></span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0 bg-white border-[#EEEFEF]">
        <Command>
          <CommandInput placeholder="Filter people…" />
          <CommandList>
            <CommandEmpty>No users.</CommandEmpty>
            <CommandGroup>
              {users.filter((u) => !u.is_agent || u.visibility === "on").map((u) => (
                <CommandItem key={u.id} value={u.name} data-testid={`workload-user-option-${u.id}`} onSelect={() => toggle(u.id)}>
                  <Check className={`h-4 w-4 mr-2 ${selected.includes(u.id) ? "opacity-100 text-[#5b2fd6]" : "opacity-0"}`} />
                  <span className="flex items-center gap-2"><UserAvatar name={u.name} uid={u.id} hasAvatar={u.has_avatar} isAgent={u.is_agent} color={u.color} size={22} /><span className="truncate">{u.name}</span></span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Pill({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${active ? "bg-[#EDE7FF] text-[#5b2fd6] border-[#d9ccff]" : "bg-white text-[#595D62] border-[#EEEFEF] hover:bg-[#F6F6FD]"}`}><Icon className="h-3.5 w-3.5" /> {children}</button>
  );
}

export default function ConsoleWorkload({ canEstimate = false }) {
  const [tab, setTab] = useState("all");
  const [canManage, setCanManage] = useState(false);
  useEffect(() => { api.get("/console/scheduler").then(({ data }) => setCanManage(!!data.can_schedule)).catch(() => {}); }, []);
  const [data, setData] = useState({ tasks: [], statuses: [] });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [openTask, setOpenTask] = useState(null);

  const load = async () => {
    try {
      const [t, cl] = await Promise.all([api.get("/console/tasks?archived=all"), api.get("/console/projects")]);
      setData(t.data); setClients(cl.data.clients || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); api.get("/agency/users?include_agents=1").then(({ data }) => setUsers(data.filter((u) => u.status !== "invited"))); }, []);

  return (
    <main className="px-6 md:px-10 py-10 max-w-[1500px] mx-auto" data-testid="console-workload-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] flex items-center gap-2.5"><Gauge className="h-7 w-7 text-[#723FFF]" /> Workload</h1>
          <p className="mt-2 text-sm text-[#9FA1A4]">Every task across the whole agency, and each person's capacity over time.</p>
        </div>
      </div>

      <div className="inline-flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-white p-1 mb-6">
        <button data-testid="workload-tab-all" onClick={() => setTab("all")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === "all" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><ListChecks className="h-4 w-4" /> All tasks</button>
        <button data-testid="workload-tab-capacity" onClick={() => setTab("capacity")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === "capacity" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><Timer className="h-4 w-4" /> Capacity</button>
        {canManage && <button data-testid="workload-tab-manage" onClick={() => setTab("manage")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === "manage" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><CalendarRange className="h-4 w-4" /> Manage</button>}
        <button data-testid="workload-tab-timesheet" onClick={() => setTab("timesheet")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === "timesheet" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><Clock className="h-4 w-4" /> Timesheet</button>
      </div>

      {tab === "all"
        ? <AllTasksView data={data} users={users} clients={clients} onOpen={setOpenTask} onChanged={load} />
        : tab === "manage" ? <ManageSchedule data={data} users={users} onOpen={setOpenTask} onChanged={load} />
        : tab === "capacity"
        ? <CapacityView data={data} users={users} onOpen={setOpenTask} />
        : <TimesheetView users={users} onOpen={setOpenTask} />}

      {openTask && <TaskDetailDialog taskId={openTask} users={users} canEdit canEstimate={canEstimate} canMove={canEstimate} onClose={() => setOpenTask(null)} onChanged={load} />}
    </main>
  );
}

function AllTasksView({ data, users, clients, onOpen, onChanged }) {
  const [selUsers, setSelUsers] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [range, setRange] = useState(undefined);
  const [sortByDate, setSortByDate] = useState(false);
  const [groupBy, setGroupBy] = useState("none");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeMsg, setNudgeMsg] = useState("");
  const todayIso = todayStr();
  const dueState = (t) => {
    if (!t.due_date || t.is_completed) return null;
    if (t.due_date < todayIso) return "overdue";
    if (t.due_date === todayIso) return "today";
    return null;
  };

  const rows = useMemo(() => {
    let list = [...(data.tasks || [])];
    if (selUsers.length) list = list.filter((t) => selUsers.includes(t.assignee_id));
    if (clientFilter !== "all") list = list.filter((t) => t.client?.id === clientFilter);
    if (statusFilter === "completed") list = list.filter((t) => t.is_completed);
    else if (statusFilter === "open") list = list.filter((t) => !t.is_completed);
    if (range?.from) {
      const from = iso(range.from); const to = iso(range.to || range.from);
      list = list.filter((t) => t.due_date && t.due_date >= from && t.due_date <= to);
    }
    if (sortByDate) list.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
    return list;
  }, [data.tasks, selUsers, clientFilter, statusFilter, range, sortByDate]);

  const groups = useMemo(() => {
    if (groupBy === "none") return [{ name: null, rows }];
    if (groupBy === "date") {
      const m = {};
      rows.forEach((t) => { const k = t.due_date || "zzzz"; (m[k] = m[k] || { key: k, name: t.due_date ? format(parseISO(t.due_date), "EEEE, d MMM yyyy") : "No due date", rows: [] }).rows.push(t); });
      return Object.values(m).sort((a, b) => a.key.localeCompare(b.key));
    }
    const key = groupBy === "client" ? (t) => t.client?.name || "—" : (t) => t.assignee?.name || "Unassigned";
    return Object.values(rows.reduce((acc, t) => { const k = key(t); (acc[k] = acc[k] || { name: k, rows: [] }).rows.push(t); return acc; }, {}));
  }, [rows, groupBy]);

  const showCompleted = statusFilter === "completed";
  const rowIds = rows.map((t) => t.id);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rowIds));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const done = () => { clearSel(); onChanged && onChanged(); };

  const bulkComplete = async () => {
    const ids = [...selected]; setBulkBusy(true);
    try {
      const { data: res } = await api.post("/console/tasks/bulk-complete", { ids });
      const items = res.items || [];
      toast.success(`${res.completed} task${res.completed !== 1 ? "s" : ""} completed`, { duration: 8000, action: { label: "Undo", onClick: async () => { for (const it of items) { try { await api.put(`/console/tasks/${it.id}`, { status: it.prev_status }); } catch (e) {} } onChanged && onChanged(); } } });
      done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBulkBusy(false); }
  };
  const bulkReassign = async (val) => {
    const ids = [...selected]; const assignee_id = val === "__unassigned__" ? null : val; setBulkBusy(true);
    try {
      const { data: res } = await api.post("/console/tasks/bulk-update", { ids, assignee_id });
      const items = res.items || [];
      const who = assignee_id ? (users.find((u) => u.id === assignee_id)?.name || "teammate") : "Unassigned";
      toast.success(`${res.updated} task${res.updated !== 1 ? "s" : ""} reassigned to ${who}`, { duration: 8000, action: { label: "Undo", onClick: async () => { for (const it of items) { try { await api.put(`/console/tasks/${it.id}`, { assignee_id: it.prev.assignee_id ?? null }); } catch (e) {} } onChanged && onChanged(); } } });
      done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBulkBusy(false); }
  };
  const bulkMove = async (status) => {
    const ids = [...selected]; setBulkBusy(true);
    try {
      const { data: res } = await api.post("/console/tasks/bulk-update", { ids, status });
      const items = res.items || [];
      toast.success(`${res.updated} task${res.updated !== 1 ? "s" : ""} moved to ${status}`, { duration: 8000, action: { label: "Undo", onClick: async () => { for (const it of items) { try { await api.put(`/console/tasks/${it.id}`, { status: it.prev.status }); } catch (e) {} } onChanged && onChanged(); } } });
      done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBulkBusy(false); }
  };
  const sendNudge = async () => {
    if (!nudgeMsg.trim()) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/console/tasks/multi-nudge", { ids: [...selected], message: nudgeMsg.trim() });
      const sent = res.sent || []; const skipped = res.skipped || [];
      if (!sent.length) {
        toast.error(`No one was nudged${skipped.length ? ` — ${skipped.join(", ")} need a Google Chat webhook in Team` : ""}.`);
      } else {
        toast.success(`Nudged ${sent.join(", ")}${skipped.length ? ` · skipped ${skipped.join(", ")} (no webhook)` : ""}`);
        setNudgeOpen(false); setNudgeMsg(""); done();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBulkBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <SavedViews screen="workload-all" state={{ selUsers, clientFilter, statusFilter, sortByDate, groupBy }} defaultState={{ selUsers: [], clientFilter: "all", statusFilter: "all", sortByDate: false, groupBy: "none" }} onApply={(s) => { setSelUsers(Array.isArray(s?.selUsers) ? s.selUsers : []); setClientFilter(s?.clientFilter || "all"); setStatusFilter(s?.statusFilter || "all"); setSortByDate(!!s?.sortByDate); setGroupBy(s?.groupBy || "none"); }} />
        <span className="h-5 w-px bg-[#EEEFEF] mx-0.5" />
        <MultiUserSelect users={users} selected={selUsers} onChange={setSelUsers} />
        <DateRangePicker value={range} onChange={setRange} testid="workload-date-range" />
        <div className="inline-flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-[#9FA1A4]" />
          <Select value={clientFilter} onValueChange={setClientFilter}><SelectTrigger data-testid="workload-filter-client" className="h-8 w-44 bg-white border-[#EEEFEF] text-xs"><SelectValue /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="all">All clients</SelectItem>{clients.map((g) => <SelectItem key={g.client.id} value={g.client.id}>{g.client.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger data-testid="workload-filter-completed" className="h-8 w-40 bg-white border-[#EEEFEF] text-xs"><SelectValue /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="all">All statuses</SelectItem><SelectItem value="open">Not completed</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select>
        <Pill testid="workload-sort-date" active={sortByDate} onClick={() => setSortByDate((v) => !v)} icon={ArrowUpDown}>Sort by date</Pill>
        <div className="inline-flex items-center gap-1.5">
          <Select value={groupBy} onValueChange={setGroupBy}><SelectTrigger data-testid="workload-group-by" className="h-8 w-40 bg-white border-[#EEEFEF] text-xs"><SelectValue /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="none">No grouping</SelectItem><SelectItem value="date">Group by date</SelectItem><SelectItem value="client">Group by client</SelectItem><SelectItem value="assignee">Group by user</SelectItem></SelectContent></Select>
        </div>
        <span className="ml-auto text-xs text-[#9FA1A4]" data-testid="workload-task-count">{rows.length} task{rows.length !== 1 ? "s" : ""}</span>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 rounded-2xl border border-[#d9ccff] bg-[#F8F5FF] px-4 py-2.5 flex-wrap" data-testid="workload-bulk-bar">
          <span className="text-sm text-[#5b2fd6] font-medium">{selected.size} selected</span>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value="" onValueChange={bulkReassign}>
              <SelectTrigger data-testid="workload-bulk-reassign" disabled={bulkBusy} className="h-8 w-40 rounded-full bg-white border-[#d9ccff] text-xs text-[#5b2fd6] font-medium data-[placeholder]:text-[#5b2fd6]/70"><span className="inline-flex items-center gap-1.5 truncate"><Users2 className="h-3.5 w-3.5 shrink-0" /><SelectValue placeholder="Reassign to…" /></span></SelectTrigger>
              <SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="__unassigned__">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value="" onValueChange={bulkMove}>
              <SelectTrigger data-testid="workload-bulk-move" disabled={bulkBusy} className="h-8 w-36 rounded-full bg-white border-[#d9ccff] text-xs text-[#5b2fd6] font-medium data-[placeholder]:text-[#5b2fd6]/70"><span className="inline-flex items-center gap-1.5 truncate"><ArrowUpDown className="h-3.5 w-3.5 shrink-0" /><SelectValue placeholder="Move to…" /></span></SelectTrigger>
              <SelectContent className="bg-white border-[#EEEFEF]">{(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="workload-bulk-nudge" onClick={() => setNudgeOpen(true)} disabled={bulkBusy} size="sm" variant="outline" className="border-[#d9ccff] bg-white text-[#5b2fd6] hover:bg-[#F3EFFF] rounded-full"><Bell className="h-4 w-4 mr-1.5" /> Nudge</Button>
            <Button data-testid="workload-bulk-complete" onClick={bulkComplete} disabled={bulkBusy} size="sm" className="bg-[#30BD79] hover:bg-[#25a267] text-white rounded-full">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> Complete</>}</Button>
            <button data-testid="workload-bulk-clear" onClick={clearSel} className="text-xs text-[#595D62] hover:text-[#06060C]">Clear</button>
          </div>
        </div>
      )}

      <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden">
        <table className="w-full text-sm"><thead><tr className="border-b border-[#f0f0f3] text-left font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">
          <th className="px-3 py-3 w-10"><Checkbox data-testid="workload-select-all" checked={allSelected} onCheckedChange={toggleAll} className="h-4 w-4 rounded-[5px] border-[#d3d4d8] data-[state=checked]:bg-[#723FFF] data-[state=checked]:border-[#723FFF] data-[state=checked]:text-white shadow-none" /></th><th className="px-4 py-3">Task</th><th className="px-4 py-3">Assignee</th><th className="px-4 py-3">Due date</th><th className="px-4 py-3">Estimate</th><th className="px-4 py-3">Logged</th>{showCompleted && <th className="px-4 py-3">Completed on</th>}<th className="px-4 py-3">Project</th><th className="px-4 py-3">Client</th>
        </tr></thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.name || "all"}>
              {g.name && <tr><td colSpan={showCompleted ? 9 : 8} className="px-4 py-2 bg-[#F6F6FD] font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6]" data-testid="workload-group-header">{g.name}</td></tr>}
              {g.rows.length === 0 && <tr><td colSpan={showCompleted ? 9 : 8} className="px-4 py-10 text-center text-sm text-[#9FA1A4]">No tasks match these filters.</td></tr>}
              {g.rows.map((t) => (
                <tr key={t.id} data-testid="workload-task-row" onClick={() => onOpen(t.id)} className={`border-b border-[#f6f6f9] hover:bg-[#FCFCFE] cursor-pointer ${selected.has(t.id) ? "bg-[#F9F6FF]" : ""}`}>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`workload-select-${t.id}`} checked={selected.has(t.id)} onCheckedChange={() => toggleSel(t.id)} className="h-4 w-4 rounded-[5px] border-[#d3d4d8] data-[state=checked]:bg-[#723FFF] data-[state=checked]:border-[#723FFF] data-[state=checked]:text-white shadow-none" /></td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2 flex-wrap"><span className="font-mono-data text-[11px] text-[#5b2fd6]">{t.code}</span> <span className="text-[#06060C]">{t.title}</span>{t.tag && <TagChip tag={t.tag} />}{t.is_completed && <span className="inline-flex items-center gap-0.5 font-mono-data text-[9px] uppercase text-[#1f9d55] bg-[#e9f7ef] rounded-full px-1.5 py-0.5"><Check className="h-2.5 w-2.5" /> Done</span>}</div></td>
                  <td className="px-4 py-3">{t.assignee ? <span className="flex items-center gap-1.5"><UserAvatar name={t.assignee.name} uid={t.assignee.id} hasAvatar={t.assignee.has_avatar} size={22} /><span className="text-[#595D62]">{t.assignee.name}</span></span> : <span className="text-[#c4c5cc]">Unassigned</span>}</td>
                  <td className="px-4 py-3">{t.due_date ? (() => { const ds = dueState(t); return (
                    <span className="inline-flex items-center gap-2">
                      <span className={ds === "overdue" ? "text-[#c0392b] font-medium" : ds === "today" ? "text-[#b06a00] font-medium" : "text-[#595D62]"}>{fmtDate(t.due_date)}</span>
                      {ds === "overdue" && <span className="font-mono-data text-[9px] uppercase tracking-wider text-[#c0392b] bg-[#FBE9E7] rounded-full px-1.5 py-0.5">Overdue</span>}
                      {ds === "today" && <span className="font-mono-data text-[9px] uppercase tracking-wider text-[#b06a00] bg-[#FFF3DE] rounded-full px-1.5 py-0.5">Today</span>}
                    </span>
                  ); })() : <span className="text-[#c4c5cc]">—</span>}</td>
                  <td className="px-4 py-3 text-[#595D62]">{t.time_estimate_minutes ? fmtMinutes(t.time_estimate_minutes) : "—"}</td>
                  <td className="px-4 py-3 text-[#595D62]">{t.time_minutes ? fmtMinutes(t.time_minutes) : "—"}</td>
                  {showCompleted && <td className="px-4 py-3 text-[#595D62]">{t.completed_at ? fmtDate(t.completed_at) : "—"}</td>}
                  <td className="px-4 py-3 text-[#595D62]">{t.project_name}</td>
                  <td className="px-4 py-3 text-[#595D62]">{t.client?.name}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody></table>
      </div>

      <Dialog open={nudgeOpen} onOpenChange={(o) => !o && setNudgeOpen(false)}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C]" data-testid="workload-nudge-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Bell className="h-4 w-4 text-[#723FFF]" /> Nudge {selected.size} task{selected.size !== 1 ? "s" : ""}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#9FA1A4]">Each assignee gets one Google Chat message listing their selected tasks. Teammates without a Google Chat webhook (set in Team) are skipped.</p>
          <Textarea data-testid="workload-nudge-message" value={nudgeMsg} onChange={(e) => setNudgeMsg(e.target.value)} placeholder="Add a short message — e.g. “Please prioritise these today.”" className="bg-[#F6F6FD] border-[#EEEFEF] min-h-[90px]" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setNudgeOpen(false)} className="border-[#EEEFEF] rounded-full">Cancel</Button>
            <Button data-testid="workload-nudge-send" onClick={sendNudge} disabled={bulkBusy || !nudgeMsg.trim()} className="bg-[#723FFF] hover:bg-[#5b2fd6] text-white rounded-full">{bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send nudge"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function freeCellClasses(free, capMin) {
  if (!capMin) return "bg-[#F2F6F3] text-[#a7b8ac]";
  if (free <= 0) return "bg-[#E06B6B] text-white";
  const r = free / capMin;
  if (r >= 0.5) return "bg-[#3FA871] text-white";
  if (r >= 0.25) return "bg-[#74C39A] text-white";
  return "bg-[#F3C271] text-[#6f4a0f]";
}

function CapacityView({ data, users, onOpen }) {
  const defaultRange = useMemo(() => { const from = startOfWeek(new Date(), { weekStartsOn: 1 }); return { from, to: addDays(from, 13) }; }, []);
  const [range, setRange] = useState(defaultRange);
  const [listModal, setListModal] = useState(null);

  const shift = (dir) => setRange((r) => {
    const len = differenceInCalendarDays(r.to, r.from) + 1;
    return { from: addDays(r.from, dir * len), to: addDays(r.to, dir * len) };
  });

  const days = useMemo(() => eachDayOfInterval({ start: range.from, end: range.to }), [range]);

  // Aggregate estimate minutes + task list by (assignee_id, due_date).
  // Completed tasks stay in the picture so past days show the real historical load.
  const byUserDay = useMemo(() => {
    const m = {};
    (data.tasks || []).forEach((t) => {
      if (!t.assignee_id || !t.due_date) return;
      const d = isValid(parseISO(t.due_date)) ? t.due_date : null;
      if (!d) return;
      const key = `${t.assignee_id}|${d}`;
      const cell = m[key] || (m[key] = { minutes: 0, tracked: 0, consumed: 0, count: 0, tasks: [] });
      const est = t.time_estimate_minutes || 0;
      const trk = t.time_minutes || 0;
      cell.minutes += est;
      cell.tracked += trk;
      if (t.is_completed) {
        // Done: count the actual work that day (tracked if logged, otherwise the estimate).
        cell.consumed += trk > 0 ? trk : est;
      } else {
        // Open: capacity draw = estimated time remaining. On track → estimate − tracked; overrun → actual tracked.
        cell.consumed += est <= 0 ? 0 : (trk <= est ? est - trk : trk);
      }
      cell.count += 1; cell.tasks.push(t);
    });
    return m;
  }, [data.tasks]);

  const monthLabel = `${format(range.from, "d MMM")} – ${format(range.to, "d MMM yyyy")}`;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button data-testid="capacity-prev" onClick={() => shift(-1)} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="capacity-next" onClick={() => shift(1)} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronRight className="h-4 w-4" /></button>
          <span className="font-display text-lg font-semibold text-[#06060C] ml-1" data-testid="capacity-month">{monthLabel}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[11px] text-[#9FA1A4]">
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#3FA871]" /> Plenty free</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#F3C271]" /> Nearly full</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#E06B6B]" /> Over capacity</span>
          </div>
          <DateRangePicker value={range} onChange={(r) => r?.from && setRange({ from: r.from, to: r.to || r.from })} testid="capacity-date-range" align="end" />
        </div>
      </div>

      <TooltipProvider delayDuration={150}>
      <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-x-auto" data-testid="capacity-grid">
        <table className="border-collapse min-w-full">
          <thead>
            <tr className="border-b border-[#f0f0f3]">
              <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] min-w-[200px]">Team</th>
              {days.map((d) => (
                <th key={iso(d)} className={`px-1 py-2 text-center min-w-[58px] ${isWeekend(d) ? "bg-[#FAFAFB]" : ""}`}>
                  <div className="font-mono-data text-[9px] uppercase tracking-wide text-[#c4c5cc]">{format(d, "EEE")}</div>
                  <div className={`text-sm font-medium ${isToday(d) ? "text-[#5b2fd6]" : "text-[#595D62]"}`}>{format(d, "d")}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.filter((u) => !u.is_agent || u.visibility === "on").length === 0 && <tr><td colSpan={days.length + 1} className="px-4 py-10 text-center text-sm text-[#9FA1A4]">No agency users.</td></tr>}
            {users.filter((u) => !u.is_agent || u.visibility === "on").map((u) => {
              const capDays = u.capacity_days || {};
              const weekTotal = Object.values(capDays).reduce((s, v) => s + (Number(v) || 0), 0);
              return (
                <tr key={u.id} data-testid={`capacity-row-${u.id}`} className="border-b border-[#f6f6f9]">
                  <td className="sticky left-0 z-10 bg-white px-4 py-2.5 min-w-[200px]">
                    <div className="flex items-center gap-2.5">
                      <UserAvatar name={u.name} uid={u.id} hasAvatar={u.has_avatar} isAgent={u.is_agent} color={u.color} size={30} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#06060C] truncate">{u.name}</p>
                        <p className="text-[11px] text-[#9FA1A4] truncate">{u.job_title || "—"} · {+weekTotal.toFixed(1)}h/wk</p>
                      </div>
                    </div>
                  </td>
                  {days.map((d) => {
                    const dayCap = Number(capDays[dowKey(d)]) || 0;
                    const capMin = Math.round(dayCap * 60);
                    const off = dayCap <= 0;
                    const cell = byUserDay[`${u.id}|${iso(d)}`];
                    const estimate = cell?.minutes || 0;
                    const tracked = cell?.tracked || 0;
                    const consumed = cell?.consumed || 0;
                    const count = cell?.count || 0;
                    const free = capMin - consumed;
                    if (off && !count) {
                      return (
                        <td key={iso(d)} className="px-1 py-1.5 bg-[#FAFAFB]">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="h-11 rounded-lg" style={{ backgroundImage: "repeating-linear-gradient(45deg,#f1f1f4,#f1f1f4 4px,transparent 4px,transparent 8px)" }} />
                            </TooltipTrigger>
                            <TooltipContent className="bg-[#06060C] text-white border-0 px-3 py-2">No capacity set for this day</TooltipContent>
                          </Tooltip>
                        </td>
                      );
                    }
                    return (
                      <td key={iso(d)} className={`px-1 py-1.5 ${off ? "bg-[#FAFAFB]" : ""}`}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              data-testid={`capacity-cell-${u.id}-${iso(d)}`}
                              onClick={() => count >= 1 && setListModal({ user: u, day: d, tasks: cell.tasks, capMin, estimate, tracked, consumed, free })}
                              className={`relative w-full h-11 rounded-lg text-xs font-semibold flex items-center justify-center transition-transform hover:scale-[1.04] ${freeCellClasses(free, capMin)} ${count >= 1 ? "cursor-pointer" : "cursor-default"}`}>
                              {hoursLabel(free)}
                              {count > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[#F59E0B] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">{count}</span>}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#06060C] text-white border-0 px-3 py-2 max-w-[230px]">
                            <div className="space-y-0.5">
                              <p className="font-medium">{free < 0 ? `${hoursLabel(-free)} over capacity` : `${hoursLabel(free)} remaining`}</p>
                              <p className="text-[11px] text-white/70">Capacity {hoursLabel(capMin)} − {hoursLabel(consumed)} remaining work · {count ? `${count} task${count > 1 ? "s" : ""}` : "no tasks"}</p>
                              <p className="text-[11px] text-white/50">Est {hoursLabel(estimate)} · Tracked {hoursLabel(tracked)}</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </TooltipProvider>
      <p className="mt-3 text-xs text-[#9FA1A4] flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Each cell shows <span className="font-medium text-[#595D62]">remaining capacity</span> that day — the person's daily capacity minus the work due that day. Open tasks draw their <span className="font-medium text-[#595D62]">estimated time remaining</span> (estimate − time already tracked); <span className="font-medium text-[#595D62]">completed tasks stay counted</span> using their actual logged time (or estimate) so past days show the true historical load. If a task overruns its estimate, the actual tracked time counts. Green = plenty free, amber = nearly full, red = over capacity.</p>

      <Dialog open={!!listModal} onOpenChange={(o) => !o && setListModal(null)}>
        <DialogContent className="bg-white border-[#EEEFEF] max-w-lg p-0 overflow-hidden" data-testid="capacity-task-list-modal">
          {listModal && (
            <>
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#f0f0f3]">
                <DialogTitle className="flex items-center gap-2.5 text-[#06060C]">
                  <UserAvatar name={listModal.user.name} uid={listModal.user.id} hasAvatar={listModal.user.has_avatar} size={28} />
                  <span className="font-display text-base font-semibold">{listModal.user.name}</span>
                </DialogTitle>
                <p className="text-xs text-[#9FA1A4] mt-1 pl-[38px]">{format(listModal.day, "EEEE, d MMM yyyy")} · {listModal.tasks.length} task{listModal.tasks.length !== 1 ? "s" : ""}</p>
                <div className="pl-[38px] mt-2.5 flex flex-wrap gap-1.5" data-testid="capacity-modal-stats">
                  <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold ${listModal.free < 0 ? "bg-[#FBE9E7] text-[#c0392b]" : "bg-[#E9F7EF] text-[#1f9d55]"}`}>{listModal.free < 0 ? `${hoursLabel(-listModal.free)} over` : `${hoursLabel(listModal.free)} remaining`}</span>
                  <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] bg-[#F6F6FD] text-[#595D62]">Capacity {hoursLabel(listModal.capMin)}</span>
                  <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] bg-[#F6F6FD] text-[#595D62]">Remaining work {hoursLabel(listModal.consumed)}</span>
                  <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] bg-[#F6F6FD] text-[#595D62]">Est {hoursLabel(listModal.estimate)}</span>
                  <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] bg-[#F6F6FD] text-[#595D62]">Tracked {hoursLabel(listModal.tracked)}</span>
                </div>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto py-1">
                {listModal.tasks.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`capacity-modal-task-${t.id}`}
                    onClick={() => { const id = t.id; setListModal(null); onOpen(id); }}
                    className="w-full text-left px-5 py-3 border-b border-[#f6f6f9] last:border-0 hover:bg-[#FCFCFE] transition-colors">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono-data text-[11px] text-[#5b2fd6]">{t.code}</span>
                      <span className="text-sm text-[#06060C]">{t.title}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#9FA1A4]">
                      <span>{t.project_name}{t.client?.name ? ` · ${t.client.name}` : ""}</span>
                      {t.time_estimate_minutes ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtMinutes(t.time_estimate_minutes)}</span> : null}
                      {t.assignee ? <span className="flex items-center gap-1"><UserAvatar name={t.assignee.name} uid={t.assignee.id} hasAvatar={t.assignee.has_avatar} size={16} />{t.assignee.name}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TimesheetView({ users, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selUsers, setSelUsers] = useState([]);
  const [range, setRange] = useState(undefined);

  useEffect(() => {
    api.get("/console/workload/timesheet").then(({ data }) => setRows(data.entries || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = rows.filter((t) => t.day);
    if (selUsers.length) list = list.filter((t) => selUsers.includes(t.user_id));
    if (range?.from) { const from = iso(range.from), to = iso(range.to || range.from); list = list.filter((t) => t.day >= from && t.day <= to); }
    return list;
  }, [rows, selUsers, range]);

  const capMinForDay = (k) => {
    const pool = selUsers.length ? users.filter((u) => selUsers.includes(u.id)) : users;
    const dk = dowKey(parseISO(`${k}T00:00:00`));
    return Math.round(pool.reduce((s, u) => s + (Number((u.capacity_days || {})[dk]) || 0), 0) * 60);
  };

  const groups = useMemo(() => {
    const m = {};
    filtered.forEach((t) => { const k = t.day; (m[k] = m[k] || { key: k, rows: [], total: 0 }); m[k].rows.push(t); m[k].total += (t.minutes || 0); });
    Object.values(m).forEach((g) => g.rows.sort((a, b) => (b.minutes || 0) - (a.minutes || 0)));
    return Object.values(m).sort((a, b) => b.key.localeCompare(a.key));
  }, [filtered]);

  const grandTotal = filtered.reduce((s, t) => s + (t.minutes || 0), 0);
  const dayLabel = (k) => format(parseISO(`${k}T00:00:00`), "EEEE, d MMM yyyy");

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <MultiUserSelect users={users} selected={selUsers} onChange={setSelUsers} />
        <DateRangePicker value={range} onChange={setRange} testid="timesheet-date-range" />
        <span className="ml-auto text-xs text-[#9FA1A4]" data-testid="timesheet-total">{fmtMinutes(grandTotal)} logged · {filtered.length} entr{filtered.length !== 1 ? "ies" : "y"}</span>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
        : groups.length === 0 ? <div className="rounded-3xl border border-[#EEEFEF] bg-white py-16 text-center text-sm text-[#9FA1A4]" data-testid="timesheet-empty">No time recorded yet.</div>
        : (
          <div className="space-y-5">
            {groups.map((g) => (
              <div key={g.key} className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden" data-testid="timesheet-day-group">
                <div className="flex items-center justify-between px-4 py-2.5 bg-[#F6F6FD] border-b border-[#f0f0f3]">
                  <span className="text-sm font-medium text-[#06060C] flex items-center gap-2" data-testid="timesheet-day-header"><Clock className="h-4 w-4 text-[#5b2fd6]" />{dayLabel(g.key)}</span>
                  <span className="font-mono-data text-xs text-[#5b2fd6]">{fmtMinutes(g.total)}</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {g.rows.map((t) => (
                      <tr key={t.id} data-testid="timesheet-task-row" onClick={() => onOpen(t.task_id)} className="border-b border-[#f6f6f9] hover:bg-[#FCFCFE] cursor-pointer">
                        <td className="px-4 py-3"><div className="flex items-center gap-2 flex-wrap">{t.code && <span className="font-mono-data text-[11px] text-[#5b2fd6]">{t.code}</span>} <span className="text-[#06060C]">{t.title}</span>{t.is_subtask && <span className="font-mono-data text-[9px] uppercase tracking-wider text-[#9FA1A4] bg-[#F6F6FD] rounded-full px-1.5 py-0.5">Subtask{t.parent_title ? ` · ${t.parent_title}` : ""}</span>}</div></td>
                        <td className="px-4 py-3 w-48">{t.user ? <span className="flex items-center gap-1.5"><UserAvatar name={t.user.name} uid={t.user.id} hasAvatar={t.user.has_avatar} size={22} /><span className="text-[#595D62]">{t.user.name}</span></span> : <span className="text-[#c4c5cc]">—</span>}</td>
                        <td className="px-4 py-3 text-[#595D62] hidden md:table-cell">{t.project_name}</td>
                        <td className="px-4 py-3 text-[#595D62] hidden lg:table-cell">{t.client?.name}</td>
                        <td className="px-4 py-3 text-right font-mono-data text-[#06060C] w-24">{fmtMinutes(t.minutes)}</td>
                      </tr>
                    ))}
                    {(() => { const cap = capMinForDay(g.key); const over = cap > 0 && g.total > cap; return (
                      <tr className="bg-[#FBFBFE] border-t border-[#EEEFEF]" data-testid="timesheet-day-total">
                        <td className="px-4 py-2.5 font-medium text-[#06060C]" colSpan={4}>Total</td>
                        <td className="px-4 py-2.5 text-right font-mono-data w-24">
                          <span className={over ? "text-[#c0392b]" : "text-[#06060C]"}>{fmtMinutes(g.total)}</span>
                          <span className="text-[#9FA1A4]"> / {fmtMinutes(cap)} cap</span>
                        </td>
                      </tr>
                    ); })()}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

